import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Typography, Tooltip, Grid, IconButton, Box} from '@mui/material'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import {
    BulkEditPropertyProps,
    EnumBulkEditProperty,
    MultiOptionBulkEditProperty,
    NumberBulkEditProperty,
    OptionBulkEditProperty
} from './BulkEditDialogProperties'
import {BulkEditEnumControl} from './BulkEditEnumControl'
import {BulkEditMultiOptionControl} from './BulkEditMultiOptionControl'
import {BulkEditNumberControl} from './BulkEditNumberControl'
import {BulkEditOptionControl} from './BulkEditOptionControl'
import {messages} from '../translations'
import {makeStyles} from '@oc/styles'

const useStyles = makeStyles(theme => ({
    root: {
        minWidth: 500,
        marginBottom: theme.spacing(1),
        position: 'relative'
    },
    propertyTitle: {
        verticalAlign: 'center'
    },
    discardButton: {
        width: '34px',
        height: '34px',
        padding: 0
    },
    changedProp: {
        backgroundColor: theme.palette.info.light
    }
}))

interface BulkEditPropertyControlProps {
    property: BulkEditPropertyProps
    groupIndex: number
    propertyIndex: number
    shouldBlockCancelConfirmCallback: React.Dispatch<React.SetStateAction<boolean>>
}

interface EditFormProps {
    property: BulkEditPropertyProps
    shouldBlockCancelConfirmCallback: React.Dispatch<React.SetStateAction<boolean>>
    id?: string
}

function _EditField(props: EditFormProps) {
    const {property, id, shouldBlockCancelConfirmCallback} = props
    if (property instanceof EnumBulkEditProperty) {
        return (
            <BulkEditEnumControl
                id={id}
                property={property}
                shouldBlockCancelConfirmCallback={shouldBlockCancelConfirmCallback}
            />
        )
    } else if (property instanceof MultiOptionBulkEditProperty) {
        return (
            <BulkEditMultiOptionControl
                id={id}
                property={property}
                shouldBlockCancelConfirmCallback={shouldBlockCancelConfirmCallback}
            />
        )
    } else if (property instanceof OptionBulkEditProperty) {
        return (
            <BulkEditOptionControl
                id={id}
                property={property}
                shouldBlockCancelConfirmCallback={shouldBlockCancelConfirmCallback}
            />
        )
    } else if (property instanceof NumberBulkEditProperty) {
        return <BulkEditNumberControl id={id} property={property} />
    }

    return null
}

const EditField = observer(_EditField)

function _BulkEditProperty(props: BulkEditPropertyControlProps) {
    const {property, propertyIndex, groupIndex, shouldBlockCancelConfirmCallback} = props
    const {classes} = useStyles()

    return (
        <Box
            key={`group-prop-container-${groupIndex}-${propertyIndex}`}
            className={property.isChanged ? classes.changedProp : undefined}
        >
            <Grid alignItems="center" container item className={classes.root} spacing={2}>
                <Grid item xs={3}>
                    <Typography className={classes.propertyTitle}>{property.name}</Typography>
                </Grid>
                <Grid item xs={8}>
                    <EditField
                        id={`group-prop-${groupIndex}-${propertyIndex}`}
                        property={property}
                        shouldBlockCancelConfirmCallback={shouldBlockCancelConfirmCallback}
                    />
                </Grid>
                <Grid item xs={1}>
                    {property.isChanged && (
                        <Tooltip title={messages.getText('buttons', 'discard')}>
                            <IconButton className={classes.discardButton} onClick={property.resetValue} size="large">
                                <ClearOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Grid>
            </Grid>
        </Box>
    )
}

export const BulkEditProperty = observer(_BulkEditProperty)
